import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { ApiError, api } from "../lib/api";

const MIN_PASSWORD = 12;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword({ token, newPassword: password });
      setDone(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The password could not be changed.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout heading="Choose a new password">
        <p className="form-error" role="alert">
          This link is missing its token. Reset links can only be used once and last an hour —
          request a new one.
        </p>
        <div className="auth-alt">
          <Link to="/forgot-password">Request a new link</Link>
          <Link to="/sign-in">Back to sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout heading="Password changed">
        <p className="form-note" role="status">
          Your password is updated and every other session has been signed out. Two-factor
          authentication is unchanged, so sign in with your new password and your authenticator
          app.
        </p>
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => navigate("/sign-in", { replace: true })}
        >
          Sign in
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      heading="Choose a new password"
      blurb="At least twelve characters. Changing it signs out every other device."
    >
      <form className="stack" onSubmit={onSubmit} noValidate>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="field">
          <label htmlFor="password">New password</label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            minLength={MIN_PASSWORD}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="confirm">New password again</label>
          <input
            id="confirm"
            className="input"
            type="password"
            autoComplete="new-password"
            required
            aria-invalid={mismatch}
            aria-describedby={mismatch ? "confirm-error" : undefined}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch ? (
            <p className="field-error" id="confirm-error">
              The two passwords do not match.
            </p>
          ) : null}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={busy || mismatch || password.length < MIN_PASSWORD}
        >
          {busy ? <span className="spinner" /> : null}
          {busy ? "Saving…" : "Save the new password"}
        </button>
      </form>
      <div className="auth-alt">
        <Link to="/sign-in">Back to sign in</Link>
      </div>
    </AuthLayout>
  );
}
